import React, { useState, useEffect } from "react";
import { getPackage, deletePackage, getAllNames } from "../api/api"; // Import getAllNames
import { Eye, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";

const Table = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [clientNames, setClientNames] = useState([]); // State for client names
  const [selectedClient, setSelectedClient] = useState(""); // State for selected client
  const [selectedDate, setSelectedDate] = useState(""); // State for selected date
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getPackage();
        // Reverse the data to implement FIFO
        setData(result.reverse());
      } catch (err) {
        setError("Failed to fetch data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    const fetchClientNames = async () => {
      try {
        const names = await getAllNames();
        setClientNames(names);
      } catch (err) {
        console.error("Failed to fetch client names:", err);
      }
    };

    fetchData();
    fetchClientNames();
  }, []);

  const handleDelete = async (id) => {
    confirmAlert({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this package?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              await deletePackage(id);
              setData(data.filter((item) => item._id !== id));
              toast.success("Package deleted successfully.");
            } catch (error) {
              console.error("Error deleting package:", error);
            }
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  const handleView = (item) => {
    navigate(`/bill/${item._id}`);
  };

  // Filtered data based on search term, selected client, and selected date
  const filteredData = data.filter((item) => {
    const search = searchTerm.toLowerCase();

    const name = item.userData?.name?.toLowerCase() || "";
    const mobile = item.userData?.mobile?.toLowerCase() || "";
    const businessName = item.userData?.businessName?.toLowerCase() || "";
    const gstNo = item.userData?.gstNo?.toLowerCase() || "";
    const invoiceNo = item.invoiceNo?.toLowerCase() || "";
    const createdDate = item.createdAt.slice(0, 10); // Assuming createdAt is in ISO format

    const matchesSearchTerm =
      name.includes(search) ||
      mobile.includes(search) ||
      businessName.includes(search) ||
      gstNo.includes(search) ||
      invoiceNo.includes(search);

    const matchesClientName = selectedClient ? item.userData?.name === selectedClient : true;

    const matchesDate = selectedDate ? createdDate === selectedDate : true;

    return matchesSearchTerm && matchesClientName && matchesDate;
  });

  // Pagination Logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="bg-gradient-to-br from-[#202020] via-[#181818] to-[#191919]  min-h-svh text-white ">
      <div className="max-w-screen-2xl p-1 mx-auto">
        <h2 className="text-2xl font-bold text-start pt-4 mb-2">Admin Panel</h2>

        {/* Search Input, Client Name Dropdown, and Date Picker */}
        <div className="mb-4 flex gap-4">
          <input
            type="text"
            placeholder="Search by Invoice Number"
            className="w-1/2 bg-black bg-opacity-50 border outline-none border-b text-white p-2 rounded-md"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="w-1/4 bg-black bg-opacity-50 border outline-none text-white p-2 rounded-md"
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
          >
            <option value="">Choose Client Name</option>
            {clientNames?.map((nameObj) => (
              <option key={nameObj._id} value={nameObj.userData?.name}>
                {nameObj.userData?.name}
              </option>
            ))}
          </select>
          <input
            type="date"
            className="w-1/9 bg-black bg-opacity-50 border outline-none text-white p-2 rounded-md"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : filteredData.length === 0 ? (
          <p className="text-center text-gray-500">No data available</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-300 shadow-md">
                <thead className="bg-white bg-opacity-10 backdrop-blur-md shadow-xl p-8 text-nowrap border-white border-opacity-20 mb-4">
                  <tr>
                    <th className="py-2 px-4 border">S.No</th>
                    <th className="py-2 px-4 border">Name</th>
                    <th className="py-2 px-4 border">Mobile</th>
                    <th className="py-2 px-4 border">Business Name</th>
                    <th className="py-2 px-4 border">Gst No</th>
                    <th className="py-2 px-4 border">Created Date </th>
                    <th className="py-2 px-4 border">Invoice No</th>
                    <th className="py-2 px-4 border">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item, index) => (
                    <tr key={item._id} className="border capitalize">
                      <td className="py-2 px-4 border text-center">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="py-2 px-4 border text-center">
                        {item.userData?.name || "N/A"}
                      </td>
                      <td className="py-2 px-4 border text-nowrap text-center">
                        {item.userData?.mobile || "N/A"}
                      </td>
                      <td className="py-2 px-4 border text-nowrap text-center">
                        {item.userData?.businessName || "N/A"}
                      </td>
                      <td className="py-2 px-4 border text-center">
                        {item.userData?.gstNo || "---"}
                      </td>
                      <td className="py-2 px-4 border text-center">
                        {item.createdAt.slice(0, 10).split('-').reverse().join('-')}
                      </td>
                      <td className="py-2 px-4 border text-center">
                        {item.invoiceNo || "N/A"}
                      </td>
                      <td className="py-2 px-4 text-center flex justify-center gap-2">
                        <button onClick={() => handleView(item)} className="text-blue-600 hover:text-blue-800">
                          <Eye size={18} />
                        </button>
                        <button onClick={() => handleDelete(item._id)} className="text-red-600 hover:text-red-800">
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-center items-center mt-4 gap-4">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-800 disabled:bg-gray-500 rounded-md"
              >
                Previous
              </button>
              <span className="text-white">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-800 disabled:bg-gray-500 rounded-md"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Table;